import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Inbox, ShieldCheck } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button, buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import {
  pcmpDisburseVerifyCountsQueryOptions,
  pcmpDisburseVerifyQueueQueryOptions,
} from '../../api/pcmpDisburseVerify.api';
import {
  isPcmpDisburseVerifyPendingTab,
  PCMP_DISBURSE_VERIFY_EMPTY_MESSAGE,
  PCMP_DISBURSE_VERIFY_TAB_COUNT,
  PCMP_DISBURSE_VERIFY_TAB_LABELS,
} from '../../constants/pcmpDisburseVerify.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import { usePcmpDisburseVerifyFilters } from '../../hooks/usePcmpDisburseVerifyFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../../hooks/usePcmpRowSelection';
import {
  PCMP_DISBURSE_VERIFY_TABS,
  type PcmpDisburseVerifyItem,
} from '../../types/pcmpDisburseVerify.types';
import { PcmpSelectionBar } from '../PcmpBatch/PcmpSelectionBar';
import { PcmpPayoutAckCell } from '../PcmpDisbursementAck/PcmpPayoutAckCell';
import {
  PcmpVerifyDisbursementDialog,
  type PcmpVerifyDisbursementTarget,
} from '../PcmpDisbursementAck/PcmpVerifyDisbursementDialog';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { PcmpQueueTabs } from '../PcmpQueue/PcmpQueueTabs';
import { getPcmpQueueColumns } from '../PcmpQueue/pcmpQueueColumns';
import { pcmpSelectColumn } from '../PcmpQueue/pcmpSelectColumn';

/**
 * Legacy DisburseVerify (LDS-770): MIS and Admin tick the payouts of a
 * Pending tab and verify them; the Verified tabs are the record.
 */
export function PcmpDisburseVerifyQueue() {
  const router = useRouter();
  const controller = usePcmpDisburseVerifyFilters();
  const { tab, limit, appliedFilters, queueParams } = controller;
  const { can } = usePcmpPermissions();
  const isPendingTab = isPcmpDisburseVerifyPendingTab(tab);
  const canVerify = isPendingTab && can(PCMP_PERMISSION.MIS, PCMP_PERMISSION.ACT);
  const isAccountsTab = tab === 'ACCOUNTS_PENDING' || tab === 'ACCOUNTS_VERIFIED';

  const selection = usePcmpRowSelection(JSON.stringify([tab, appliedFilters]));
  const [verifyTarget, setVerifyTarget] = useState<PcmpVerifyDisbursementTarget | null>(null);

  const { data: counts } = useQuery(pcmpDisburseVerifyCountsQueryOptions(appliedFilters));
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpDisburseVerifyQueueQueryOptions(queueParams),
  );

  useKeepPageInRange(queueParams.page, data?.meta.totalPages, controller.setPage);

  const openProposal = useCallback(
    (item: PcmpDisburseVerifyItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const rows = useMemo(() => data?.data ?? [], [data]);

  const ackColumns: ColumnDef<PcmpDisburseVerifyItem>[] = [
    // Only Accounts payouts are confirmed by the MO (LDS-747).
    ...(isAccountsTab
      ? [
          {
            id: 'confirmation',
            header: 'MO Confirmation',
            cell: ({ row }) => (
              <PcmpPayoutAckCell kind="confirmation" ack={row.original.payout.confirmation} />
            ),
          } satisfies ColumnDef<PcmpDisburseVerifyItem>,
        ]
      : []),
    ...(isPendingTab
      ? []
      : [
          {
            id: 'verification',
            header: 'Verification',
            cell: ({ row }) => (
              <PcmpPayoutAckCell kind="verification" ack={row.original.payout.verification} />
            ),
          } satisfies ColumnDef<PcmpDisburseVerifyItem>,
        ]),
  ];

  const columns = getPcmpQueueColumns<PcmpDisburseVerifyItem>({
    leading: canVerify ? [pcmpSelectColumn<PcmpDisburseVerifyItem>({ selection })] : [],
    afterLanes: ackColumns,
    renderActions: (item) => (
      <Link
        to="/pcmp/proposals/$proposalId"
        params={{ proposalId: item.proposalId }}
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
      >
        <Eye className="size-3.5" />
        View
      </Link>
    ),
  });

  const total = data?.meta.total ?? 0;
  // No matching total comes with the counts, so the bar shows counts only.
  const summary = selection.summarize(total, 0);
  const isPageSelected =
    rows.length > 0 && rows.every((row) => selection.isSelected(row.proposalId));
  const singleRefNo =
    !selection.allMatching && summary.count === 1
      ? rows.find((row) => row.proposalId === selection.pickedIds[0])?.refNo
      : undefined;

  const startVerify = () => {
    setVerifyTarget({
      tab,
      count: summary.count,
      refNo: singleRefNo,
      selection: selection.allMatching
        ? {
            selectAllMatching: true,
            filters: appliedFilters,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });
  };

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Disburse Verify</h1>
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {counts
            ? `${formatNumber(counts.accountsPending)} Accounts and ${formatNumber(
                counts.marketingPending,
              )} Marketing payouts awaiting verification`
            : ' '}
        </p>
      </div>

      <PcmpQueueTabs
        tabs={PCMP_DISBURSE_VERIFY_TABS}
        labels={PCMP_DISBURSE_VERIFY_TAB_LABELS}
        value={tab}
        countOf={counts ? (value) => PCMP_DISBURSE_VERIFY_TAB_COUNT[value](counts) : undefined}
        onChange={controller.setTab}
      />

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-disb-verify" />

      {canVerify ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={{ total, amount: 0, isPageSelected }}
          hint="Tick disbursed proposals to verify their payouts."
          withAmount={false}
        >
          <Button
            type="button"
            size="sm"
            onClick={startVerify}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <ShieldCheck className="size-3.5" />
            Verify
          </Button>
        </PcmpSelectionBar>
      ) : null}

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle={`Could not load ${PCMP_DISBURSE_VERIFY_TAB_LABELS[tab]}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage={PCMP_DISBURSE_VERIFY_EMPTY_MESSAGE[tab]}
          skeletonRowCount={Math.min(limit, 15)}
          getRowClassName={(item) =>
            canVerify && selection.isSelected(item.proposalId) ? 'bg-primary/5' : undefined
          }
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}

      {verifyTarget ? (
        <PcmpVerifyDisbursementDialog
          target={verifyTarget}
          onVerified={selection.clear}
          onClose={() => setVerifyTarget(null)}
        />
      ) : null}
    </div>
  );
}
